import { exclusionKey } from './analysisExclusions';

// Rebalancing assistant: orders that bring the held lines towards the target
// weights, within the PEA's constraints:
// - whole shares only, no order below the minimum amount;
// - no margin: purchases never exceed the cash plus the sales, and the cash
//   reserve target is kept (purchases are scaled down when short);
// - lines without a target are left alone ("hors plan").
// Prices are the last known ones; fees and slippage are not included.

const round2 = (v) => Math.round(v * 100) / 100;

const priceOf = (p) => {
  if (p.current_price > 0) return p.current_price;
  if (p.amount > 0 && p.market_value > 0) return p.market_value / p.amount;
  return 0;
};

export const currentWeights = (positions, totalValue) => {
  const out = {};
  positions.forEach((p) => {
    out[exclusionKey(p.symbol)] = totalValue > 0 ? round2(((p.market_value || 0) / totalValue) * 100) : 0;
  });
  return out;
};

// targets: { [symbolKey]: pct } (missing key = no target for that line)
export const planRebalance = ({ positions, cash = 0, targets = {}, cashTargetPct = 0, minTrade = 300 }) => {
  const invested = positions.reduce((sum, p) => sum + (p.market_value || 0), 0);
  const total = invested + cash;
  const warnings = [];
  const targetSum = Object.values(targets).reduce((s, v) => s + (Number(v) || 0), 0) + (Number(cashTargetPct) || 0);

  const lines = positions.map((p) => {
    const key = exclusionKey(p.symbol);
    const price = priceOf(p);
    const value = p.market_value || 0;
    const hasTarget = targets[key] !== undefined && targets[key] !== null && targets[key] !== '';
    const target = hasTarget ? Number(targets[key]) : null;
    const line = {
      key,
      symbol: p.symbol,
      description: p.description,
      price,
      quantity: p.amount || 0,
      value,
      weight: total > 0 ? (value / total) * 100 : 0,
      target,
      targetValue: hasTarget ? (total * target) / 100 : value,
      action: 'hold',
      shares: 0,
      tradeValue: 0,
      reason: hasTarget ? null : 'hors plan',
    };
    if (!hasTarget || price <= 0) {
      if (hasTarget && price <= 0) line.reason = 'cours indisponible';
      return line;
    }
    const diff = line.targetValue - value;
    if (Math.abs(diff) < minTrade) {
      line.reason = 'écart sous le minimum';
      return line;
    }
    if (diff < 0) {
      const shares = Math.min(line.quantity, Math.floor(-diff / price));
      if (shares > 0 && shares * price >= minTrade) Object.assign(line, { action: 'sell', shares, tradeValue: round2(shares * price), reason: null });
      else line.reason = 'écart sous le minimum';
    } else {
      const shares = Math.floor(diff / price);
      if (shares > 0) Object.assign(line, { action: 'buy', shares, tradeValue: round2(shares * price), reason: null });
      else line.reason = 'écart inférieur à une action';
    }
    return line;
  });

  const sells = lines.filter((l) => l.action === 'sell').reduce((s, l) => s + l.tradeValue, 0);
  const reserve = (total * (Number(cashTargetPct) || 0)) / 100;
  const available = Math.max(0, cash + sells - reserve);
  const wanted = lines.filter((l) => l.action === 'buy').reduce((s, l) => s + l.tradeValue, 0);
  let scaled = false;
  if (wanted > available + 0.005) {
    scaled = true;
    const factor = wanted > 0 ? available / wanted : 0;
    warnings.push(available <= 0
      ? 'Aucun achat possible : les liquidités et les ventes ne couvrent pas la réserve visée.'
      : 'Achats réduits : liquidités et ventes ne suffisent pas à tout acheter sans entamer la réserve.');
    lines.forEach((l) => {
      if (l.action !== 'buy') return;
      const shares = Math.floor((l.tradeValue * factor) / l.price);
      if (shares > 0 && shares * l.price >= minTrade) Object.assign(l, { shares, tradeValue: round2(shares * l.price) });
      else Object.assign(l, { action: 'hold', shares: 0, tradeValue: 0, reason: 'liquidités insuffisantes' });
    });
  }
  const buys = lines.filter((l) => l.action === 'buy').reduce((s, l) => s + l.tradeValue, 0);
  if (Math.abs(targetSum - 100) > 0.5 && Object.keys(targets).length > 0) {
    warnings.push(targetSum < 100
      ? `Les cibles et la réserve totalisent ${round2(targetSum)} % : le reste ira aux liquidités ou aux lignes hors plan.`
      : `Les cibles et la réserve totalisent ${round2(targetSum)} %, au-delà de 100 %.`);
  }
  const cashAfter = round2(cash + sells - buys);
  return {
    lines,
    orders: lines.filter((l) => l.action !== 'hold').sort((a, b) => (a.action === b.action ? b.tradeValue - a.tradeValue : a.action === 'sell' ? -1 : 1)),
    total: round2(total),
    sells: round2(sells),
    buys: round2(buys),
    cashAfter,
    cashAfterPct: total > 0 ? round2((cashAfter / total) * 100) : 0,
    scaled,
    warnings,
  };
};
